import { useState, useCallback, useMemo, useRef, useEffect } from 'react';
import { CanvasElement, ElementType, AspectRatio, PromptFormat, AppMode, GlobalSettings, DEFAULT_GLOBAL_SETTINGS } from '@/lib/engineTypes';
import { CustomPalette } from '@/data/options/colorPalettes';
import { generatePrompt } from '@/lib/promptGenerator';
import { promptStoreActions } from '@/store/promptStore';
import { UnifiedSceneState, createDefaultSceneState, type CharacterReference, type ImageReferences } from '@/types/unifiedSceneState';
import { parsePromptToScene } from '@/lib/promptIntelligenceEngine';
import type { HistoryEntry, SavedSettings } from '@/components/HistoryPanel';
import {
  analyzeSceneHealth,
  fixSingleIssue,
  fixAllIssues,
  mapIssueToTokenGroup,
  type PromptHealthReport,
  type SingleFixRequest,
} from '@/engine/promptHealthPipeline';
import { clearAllConstraints, type TokenConflict } from '@/engine/tokenRegistry';
import type { SceneFixContext, SceneFixResult } from '@/engine/fixStrategyEngine';
import { sanitizeSceneDescription } from '@/engine/fixStrategyEngine';
import type { PromptIssue } from '@/lib/promptInlineAnalyzer';
import { runCinematicPipeline, sceneGraphToCanvasProperties, type PipelineResult } from '@/engine/cinematicPipeline';
import { getSmartDefaults } from '@/lib/smartContextEngine';
import type { SceneGraph } from '@/engine/sceneGraph';

let idCounter = 0;
const genId = () => `el-${++idCounter}`;

const defaultProps: Record<ElementType, Record<string, string>> = {
  light: { intensity: '50', lightStyle: 'none', colorTemp: 'neutral', lightModifier: 'none', lightPreset: 'none', colorTempValue: '5500', shadowSoftness: '50', lightDirection: '0', godRays: 'false', fogDensity: '0' },
  // defaultProps used when the pipeline fills in a new element
  // start with all camera settings unset ('none') so clean/reset behaves
  // correctly; inference from prompt text may later populate these fields.
  camera: { lens: 'none', cameraType: 'none', perspective: 'none', shotType: 'none', dof: 'none', focusMode: 'none', cameraMovement: 'static', sensorSize: 'full_frame', shutterSpeed: '1/125', isoControl: '100', motionBlur: '0', dofIntensity: '50' },
  // character defaults are mostly empty, not even 'a person'
  character: { subject: '', pose: 'none', expression: 'none', characterFraming: 'full', microExpression: 'none', eyeDirection: 'none', bodyRotation: '0', gestureIntensity: '50' },
};

function lerp(a: number, b: number, t: number): number {
  return a + (b - a) * t;
}

function snapToGrid(val: number, gridSize: number, snapThreshold: number): number {
  const nearest = Math.round(val / gridSize) * gridSize;
  return Math.abs(val - nearest) < snapThreshold ? nearest : val;
}

function magneticAlign(val: number, targets: number[], threshold: number): number {
  for (const t of targets) {
    if (Math.abs(val - t) < threshold) return t;
  }
  return val;
}

const GRID_SIZE = 5;
const SNAP_THRESHOLD = 3;
const MAGNETIC_LINES = [25, 33.33, 50, 66.66, 75];
const MAGNETIC_THRESHOLD = 4;

const stringifyCharacter = (element?: CanvasElement): string => {
  if (!element) return '';
  const p = element.properties;
  return [p.subject, p.pose, p.expression].filter(Boolean).join(', ');
};

const stringifyCamera = (element: CanvasElement | undefined, aspectRatio: AspectRatio): string => {
  if (!element) return '';
  const p = element.properties;
  return [p.shotType, p.lens, p.perspective, p.dof, `aspect ${aspectRatio}`].filter(Boolean).join(', ');
};

const stringifyLighting = (element?: CanvasElement): string => {
  if (!element) return '';
  const p = element.properties;
  return [p.lightStyle, p.lightPreset, p.intensity ? `${p.intensity}% intensity` : '', p.colorTempValue ? `${p.colorTempValue}K` : '']
    .filter(Boolean)
    .join(', ');
};

/**
 * Apply SceneGraph pipeline results to canvas elements.
 * This is the new way — replaces applyParsedPromptToElements.
 * NULL values → element defaults. User intent is preserved.
 */
const applyPipelineToElements = (
  base: CanvasElement[],
  pipelineResult: PipelineResult,
): CanvasElement[] => {
  const props = sceneGraphToCanvasProperties(pipelineResult.sceneGraph);
  let next = [...base];

  // ── Character ──
  if (props.character) {
    const existing = next.find((el) => el.type === 'character');
    if (existing) {
      next = next.map((el) =>
        el.id === existing.id
          ? { ...el, label: props.character!.subject || el.label, properties: { ...el.properties, ...props.character } }
          : el
      );
    } else {
      next.push({
        id: genId(),
        type: 'character',
        x: 46, y: 52, scale: 1,
        label: props.character.subject || 'Character',
        properties: { ...defaultProps.character, ...props.character },
      });
    }
  }

  // ── Camera ──
  {
    const existing = next.find((el) => el.type === 'camera');
    if (existing) {
      next = next.map((el) =>
        el.id === existing.id
          ? { ...el, properties: { ...el.properties, ...props.camera } }
          : el
      );
    } else {
      next.push({
        id: genId(),
        type: 'camera',
        x: 40, y: 40, scale: 1,
        label: 'Camera',
        properties: { ...defaultProps.camera, ...props.camera },
      });
    }
  }

  // ── Light ──
  {
    const existing = next.find((el) => el.type === 'light');
    if (existing) {
      next = next.map((el) =>
        el.id === existing.id
          ? { ...el, properties: { ...el.properties, ...props.light } }
          : el
      );
    } else {
      next.push({
        id: genId(),
        type: 'light',
        x: 58, y: 42, scale: 1,
        label: 'Light',
        properties: { ...defaultProps.light, ...props.light },
      });
    }
  }

  return next;
};

const STORAGE_KEY = 'controlz_state';

const loadSavedState = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch { return null; }
};

const savePersistState = (data: Record<string, unknown>) => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
  } catch { /* ignore */ }
};

export function useCanvasElements() {
  const saved = loadSavedState();
  const [elements, setElements] = useState<CanvasElement[]>(saved?.elements ?? []);
  const [aspectRatio, setAspectRatioState] = useState<AspectRatio>(saved?.aspectRatio ?? '16:9');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [history, setHistory] = useState<HistoryEntry[]>(saved?.history ?? []);
  const [sceneDescription, setSceneDescriptionState] = useState(saved?.sceneDescription ?? '');
  const [stylePreset, setStylePresetState] = useState(saved?.stylePreset ?? 'none');
  const [promptFormat, setPromptFormat] = useState<PromptFormat>(saved?.promptFormat ?? 'structured');
  const [palettePreset, setPalettePresetState] = useState(saved?.palettePreset ?? 'none');
  const [customPalette, setCustomPalette] = useState<CustomPalette | null>(saved?.customPalette ?? null);
  const [mode, setMode] = useState<AppMode>(saved?.mode ?? 'basic');
  const [globalSettings, setGlobalSettings] = useState<GlobalSettings>(saved?.globalSettings ?? { ...DEFAULT_GLOBAL_SETTINGS });
  const [sceneState, setSceneState] = useState<UnifiedSceneState>(createDefaultSceneState());
  const [currentSceneGraph, setCurrentSceneGraph] = useState<SceneGraph | null>(null);
  const [pipelineResult, setPipelineResult] = useState<PipelineResult | null>(null);

  const [debouncedElements, setDebouncedElements] = useState<CanvasElement[]>([]);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const targetPositions = useRef<Record<string, { x: number; y: number }>>({});
  const rafRef = useRef<number | null>(null);

  const updateSceneState = useCallback((patch: Partial<UnifiedSceneState>) => {
    setSceneState((prev) => {
      const next = { ...prev, ...patch };
      const changed = Object.keys(next).some((key) => {
        const typedKey = key as keyof UnifiedSceneState;
        return prev[typedKey] !== next[typedKey];
      });
      return changed ? next : prev;
    });
  }, []);

  const syncSceneStateFromUI = useCallback((input: {
    nextElements?: CanvasElement[];
    nextSceneDescription?: string;
    nextStylePreset?: string;
    nextPalettePreset?: string;
    nextAspectRatio?: AspectRatio;
    nextGlobalSettings?: GlobalSettings;
  } = {}) => {
    const nextElements = input.nextElements ?? elements;
    const nextSceneDescription = input.nextSceneDescription ?? sceneDescription;
    const nextStylePreset = input.nextStylePreset ?? stylePreset;
    const nextPalettePreset = input.nextPalettePreset ?? palettePreset;
    const nextAspectRatio = input.nextAspectRatio ?? aspectRatio;
    const nextGlobalSettings = input.nextGlobalSettings ?? globalSettings;

    const parsed = parsePromptToScene(nextSceneDescription);
    const character = nextElements.find((el) => el.type === 'character');
    const camera = nextElements.find((el) => el.type === 'camera');
    const light = nextElements.find((el) => el.type === 'light');

    const styleParts = [parsed.style, nextStylePreset !== 'none' ? nextStylePreset : ''].filter(Boolean);

    const compositionParts = [
      nextGlobalSettings.ruleOfThirds ? 'rule of thirds' : '',
      nextGlobalSettings.centeredCinematic ? 'centered composition' : '',
      nextGlobalSettings.framingPreset && nextGlobalSettings.framingPreset !== 'none' ? nextGlobalSettings.framingPreset.replace(/_/g, ' ') : '',
      `aspect ${nextAspectRatio}`,
    ].filter(Boolean);

    updateSceneState({
      initialPrompt: nextSceneDescription,
      subject: stringifyCharacter(character) || parsed.character,
      environment: parsed.environment || nextSceneDescription,
      camera: stringifyCamera(camera, nextAspectRatio) || parsed.camera,
      lighting: stringifyLighting(light) || parsed.lighting,
      style: Array.from(new Set(styleParts.map((part) => part.toLowerCase()))).join(', '),
      mood: parsed.mood || (nextGlobalSettings.filmStock && nextGlobalSettings.filmStock !== 'none' ? String(nextGlobalSettings.filmStock) : ''),
      composition: compositionParts.join(', '),
      palette: parsed.palette || (nextPalettePreset !== 'none' ? nextPalettePreset : ''),
    });
  }, [elements, sceneDescription, stylePreset, palettePreset, aspectRatio, globalSettings, updateSceneState]);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setDebouncedElements([...elements]);
    }, 50);
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, [elements]);

  const runLerp = useCallback(() => {
    let needsUpdate = false;
    setElements(prev => {
      const next = prev.map(el => {
        const target = targetPositions.current[el.id];
        if (!target) return el;
        const dx = target.x - el.x;
        const dy = target.y - el.y;
        if (Math.abs(dx) < 0.3 && Math.abs(dy) < 0.3) {
          return { ...el, x: target.x, y: target.y };
        }
        needsUpdate = true;
        return {
          ...el,
          x: lerp(el.x, target.x, 0.25),
          y: lerp(el.y, target.y, 0.25),
        };
      });
      return next;
    });
    if (needsUpdate) {
      rafRef.current = requestAnimationFrame(runLerp);
    } else {
      rafRef.current = null;
    }
  }, []);

  const addElement = useCallback((type: ElementType) => {
    const el: CanvasElement = {
      id: genId(),
      type,
      x: 40 + Math.random() * 20,
      y: 40 + Math.random() * 20,
      scale: 1,
      label: type.charAt(0).toUpperCase() + type.slice(1),
      properties: { ...defaultProps[type] },
    };
    setElements(prev => {
      const next = [...prev, el];
      syncSceneStateFromUI({ nextElements: next });
      return next;
    });
    setSelectedId(el.id);
  }, [syncSceneStateFromUI]);

  const ensureElement = useCallback((type: ElementType) => {
    const existing = elements.find((el) => el.type === type);
    if (existing) {
      setSelectedId(existing.id);
      return existing.id;
    }

    const el: CanvasElement = {
      id: genId(),
      type,
      x: 44 + Math.random() * 12,
      y: 44 + Math.random() * 12,
      scale: 1,
      label: type.charAt(0).toUpperCase() + type.slice(1),
      properties: { ...defaultProps[type] },
    };

    setElements((prev) => {
      const next = [...prev, el];
      syncSceneStateFromUI({ nextElements: next });
      return next;
    });
    setSelectedId(el.id);
    return el.id;
  }, [elements, syncSceneStateFromUI]);

  const moveElement = useCallback((id: string, x: number, y: number) => {
    let sx = Math.max(0, Math.min(100, x));
    let sy = Math.max(0, Math.min(100, y));
    sx = snapToGrid(sx, GRID_SIZE, SNAP_THRESHOLD);
    sy = snapToGrid(sy, GRID_SIZE, SNAP_THRESHOLD);
    sx = magneticAlign(sx, MAGNETIC_LINES, MAGNETIC_THRESHOLD);
    sy = magneticAlign(sy, MAGNETIC_LINES, MAGNETIC_THRESHOLD);

    targetPositions.current[id] = { x: sx, y: sy };

    if (!rafRef.current) {
      rafRef.current = requestAnimationFrame(runLerp);
    }
  }, [runLerp]);

  const scaleElement = useCallback((id: string, scale: number) => {
    setElements(prev =>
      prev.map(el => (el.id === id ? { ...el, scale: Math.max(0.3, Math.min(3, scale)) } : el))
    );
  }, []);

  const updateProperty = useCallback((id: string, key: string, value: string) => {
    setElements((prev) => {
      const next = prev.map((el) =>
        el.id === id ? { ...el, properties: { ...el.properties, [key]: value } } : el
      );
      syncSceneStateFromUI({ nextElements: next });
      return next;
    });
  }, [syncSceneStateFromUI]);

  const removeElement = useCallback((id: string) => {
    setElements(prev => {
      const next = prev.filter(el => el.id !== id);
      syncSceneStateFromUI({ nextElements: next });
      return next;
    });
    delete targetPositions.current[id];
    setSelectedId(prev => (prev === id ? null : prev));
  }, [syncSceneStateFromUI]);

  const updateGlobal = useCallback((key: string, value: string | number | boolean) => {
    setGlobalSettings(prev => {
      const next = { ...prev, [key]: value };
      syncSceneStateFromUI({ nextGlobalSettings: next });
      return next;
    });
  }, [syncSceneStateFromUI]);

  const setAspectRatio = useCallback((next: AspectRatio) => {
    setAspectRatioState(next);
    syncSceneStateFromUI({ nextAspectRatio: next });
  }, [syncSceneStateFromUI]);

  const setStylePreset = useCallback((next: string) => {
    setStylePresetState(next);
    syncSceneStateFromUI({ nextStylePreset: next });
  }, [syncSceneStateFromUI]);

  const setPalettePreset = useCallback((next: string) => {
    setPalettePresetState(next);
    syncSceneStateFromUI({ nextPalettePreset: next });
  }, [syncSceneStateFromUI]);

  const setSceneDescription = useCallback((next: string) => {
    setSceneDescriptionState(next);

    // Run the cinematic pipeline on the new prompt
    const result = runCinematicPipeline(next);
    setPipelineResult(result);
    setCurrentSceneGraph(result.sceneGraph);

    // Apply pipeline results to canvas elements
    setElements((prev) => {
      const nextElements = applyPipelineToElements(prev, result);
      syncSceneStateFromUI({ nextElements, nextSceneDescription: next });

      // Also apply global settings from the pipeline
      const globalPatch = sceneGraphToCanvasProperties(result.sceneGraph).globalSettings;
      for (const [key, value] of Object.entries(globalPatch)) {
        updateGlobal(key, value);
      }

      return nextElements;
    });
  }, [syncSceneStateFromUI, updateGlobal]);

  const promptResult = useMemo(() => {
    const smartDefaults = getSmartDefaults(sceneDescription);

    const effectiveElements = debouncedElements.map((el) => {
      if (el.type === 'camera') {
        const current = el.properties;
        return {
          ...el,
          properties: {
            ...current,
            perspective: current.perspective && current.perspective !== 'none' ? current.perspective : (smartDefaults.cameraAngle || 'none'),
            lens: current.lens && current.lens !== 'none' ? current.lens : (smartDefaults.lens || 'none'),
            shotType: current.shotType && current.shotType !== 'none' ? current.shotType : (smartDefaults.shotSize || 'none'),
            dof: current.dof && current.dof !== 'none' ? current.dof : (smartDefaults.depthOfField || 'none'),
          },
        };
      }

      if (el.type === 'light') {
        const current = el.properties;
        return {
          ...el,
          properties: {
            ...current,
            lightStyle: current.lightStyle && current.lightStyle !== 'none'
              ? current.lightStyle
              : (smartDefaults.lighting?.includes('high contrast')
                ? 'chiaroscuro'
                : smartDefaults.lighting?.includes('soft')
                  ? 'natural'
                  : current.lightStyle),
            godRays: current.godRays === 'true' || !smartDefaults.rimLight ? current.godRays : 'true',
            fogDensity: Number(current.fogDensity || '0') > 0 || !smartDefaults.atmosphere ? current.fogDensity : '30',
          },
        };
      }

      return el;
    });

    let finalElements = effectiveElements;

    const hasCamera = finalElements.some((el) => el.type === 'camera');
    if (!hasCamera && (smartDefaults.cameraAngle || smartDefaults.lens || smartDefaults.shotSize || smartDefaults.depthOfField)) {
      finalElements = [...finalElements, {
        id: 'smart-default-camera',
        type: 'camera',
        x: 50,
        y: 50,
        scale: 1,
        label: 'Camera',
        properties: {
          ...defaultProps.camera,
          perspective: smartDefaults.cameraAngle || 'none',
          lens: smartDefaults.lens || 'none',
          shotType: smartDefaults.shotSize || 'none',
          dof: smartDefaults.depthOfField || 'none',
        },
      } as CanvasElement];
    }

    const hasLight = finalElements.some((el) => el.type === 'light');
    if (!hasLight && (smartDefaults.lighting || smartDefaults.rimLight || smartDefaults.atmosphere)) {
      finalElements = [...finalElements, {
        id: 'smart-default-light',
        type: 'light',
        x: 50,
        y: 45,
        scale: 1,
        label: 'Light',
        properties: {
          ...defaultProps.light,
          lightStyle: smartDefaults.lighting?.includes('high contrast')
            ? 'chiaroscuro'
            : smartDefaults.lighting?.includes('soft')
              ? 'natural'
              : 'none',
          godRays: smartDefaults.rimLight ? 'true' : 'false',
          fogDensity: smartDefaults.atmosphere ? '30' : '0',
        },
      } as CanvasElement];
    }

    const effectiveDescription = [sceneDescription.trim(), smartDefaults.composition || '', smartDefaults.atmosphere || '']
      .filter(Boolean)
      .join(', ');

    return generatePrompt(finalElements, aspectRatio, globalSettings, effectiveDescription, stylePreset, promptFormat, palettePreset, customPalette);
  }, [debouncedElements, aspectRatio, globalSettings, sceneDescription, stylePreset, promptFormat, palettePreset, customPalette]);

  // The prompt is now driven directly by generatePrompt which reads ALL UI state:
  // canvas positions, element properties, globalSettings (VFX, color grading, retouch, etc.),
  // style preset, palette, aspect ratio, and scene description.
  const prompt = promptResult.text;
  const conflicts = promptResult.conflicts;
  const inferred = promptResult.inferred;
  const locked = promptResult.locked;
  const vfxLayers = promptResult.vfxLayers;
  const vfxIntensity = promptResult.vfxIntensity;
  const styleTendencies = promptResult.styleTendencies;

  const saveToHistory = useCallback(() => {
    if (prompt && !prompt.startsWith('Start by writing an initial prompt')) {
      const entry: HistoryEntry = {
        id: `h_${Date.now()}`,
        prompt,
        timestamp: Date.now(),
        settings: {
          stylePreset,
          palettePreset: palettePreset,
          aspectRatio,
          globalSettings: { ...globalSettings },
          elements: elements.map(el => ({ type: el.type, properties: { ...el.properties } })),
        },
      };
      setHistory(prev => [entry, ...prev.slice(0, 19)]);
    }
  }, [prompt, stylePreset, palettePreset, aspectRatio, globalSettings, elements]);

  const deleteHistoryEntry = useCallback((id: string) => {
    setHistory(prev => prev.filter(e => e.id !== id));
  }, []);

  const loadHistoryEntry = useCallback((entry: HistoryEntry) => {
    if (entry.settings) {
      setStylePreset(entry.settings.stylePreset || 'none');
      setPalettePreset(entry.settings.palettePreset || 'none');
      setAspectRatio((entry.settings.aspectRatio || '16:9') as AspectRatio);
      if (entry.settings.globalSettings) {
        setGlobalSettings(prev => ({ ...prev, ...entry.settings!.globalSettings }));
      }
    }
    // Set scene description to prompt so user can write a new one on top
    setSceneDescription(entry.prompt);
  }, [setAspectRatio, setPalettePreset, setSceneDescription, setStylePreset]);

  const magneticGuides = useMemo(() => {
    if (!selectedId) return { x: null as number | null, y: null as number | null };
    const el = elements.find(e => e.id === selectedId);
    if (!el) return { x: null, y: null };
    const xGuide = MAGNETIC_LINES.find(l => Math.abs(el.x - l) < 1) ?? null;
    const yGuide = MAGNETIC_LINES.find(l => Math.abs(el.y - l) < 1) ?? null;
    return { x: xGuide, y: yGuide };
  }, [elements, selectedId]);

  const selected = elements.find(el => el.id === selectedId) || null;

  const promptSections = useMemo(() => ({
    summary: {
      sceneDescription,
      stylePreset,
      promptFormat,
      palettePreset,
      customPalette,
    },
    elementsByType: {
      light: elements.filter(el => el.type === 'light').length,
      camera: elements.filter(el => el.type === 'camera').length,
      character: elements.filter(el => el.type === 'character').length,
    },
    metadata: {
      inferred,
      vfxLayers,
      vfxIntensity,
      styleTendencies,
      locked,
      sceneState,
    },
  }), [sceneDescription, stylePreset, promptFormat, palettePreset, customPalette, elements, inferred, vfxLayers, vfxIntensity, styleTendencies, locked, sceneState]);

  const promptSettings = useMemo(() => ({
    aspectRatio,
    mode,
    globalSettings,
  }), [aspectRatio, mode, globalSettings]);

  useEffect(() => {
    promptStoreActions.syncFromCanvas({
      basePrompt: prompt,
      canvasElements: elements,
      sections: promptSections,
      settings: promptSettings,
      conflicts,
    });
  }, [prompt, elements, promptSections, promptSettings, conflicts]);

  useEffect(() => {
    syncSceneStateFromUI();
  }, [syncSceneStateFromUI]);

  // Persist state to localStorage
  useEffect(() => {
    const timer = setTimeout(() => {
      savePersistState({
        elements: elements.map(el => ({ ...el })),
        aspectRatio, history, sceneDescription, stylePreset,
        promptFormat, palettePreset, customPalette, mode, globalSettings,
      });
    }, 500);
    return () => clearTimeout(timer);
  }, [elements, aspectRatio, history, sceneDescription, stylePreset, promptFormat, palettePreset, customPalette, mode, globalSettings]);

  useEffect(() => {
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, []);

  const applyPromptDraft = useCallback((draft: string) => {
    const clean = draft.trim();
    if (!clean) return;
    // Pipeline handles everything — just set the scene description
    setSceneDescription(clean);
  }, [setSceneDescription]);

  const resetAll = useCallback(() => {
    setElements([]);
    setAspectRatioState('16:9');
    setSelectedId(null);
    setSceneDescriptionState('');
    setStylePresetState('none');
    setPromptFormat('structured');
    setPalettePresetState('none');
    setCustomPalette(null);
    setMode('basic');
    setGlobalSettings({ ...DEFAULT_GLOBAL_SETTINGS });
    setSceneState(createDefaultSceneState());
    setCurrentSceneGraph(null);
    setPipelineResult(null);
    clearAllConstraints();
    localStorage.removeItem(STORAGE_KEY);
  }, []);

  // ─── Image Analysis Handler ───
  const handleImageAnalysis = useCallback((result: any) => {
    const patch: Partial<UnifiedSceneState> = {};

    // Apply subject/environment/lighting/etc from analysis
    if (result.subject) patch.subject = result.subject;
    if (result.environment) patch.environment = result.environment;
    if (result.lighting) patch.lighting = result.lighting;
    if (result.camera) patch.camera = result.camera;
    if (result.mood) patch.mood = result.mood;
    if (result.palette) patch.palette = result.palette;

    // Style reference: only apply style-related fields
    if (result.intent === 'style_reference') {
      if (result.style) patch.style = result.style;
      if (result.palette) patch.palette = result.palette;
      if (result.mood) patch.mood = result.mood;
    } else if (result.style) {
      patch.style = result.style;
    }

    // Character reference
    if (result.faceDescription || result.hair || result.expression) {
      const character: CharacterReference = {
        faceDescription: result.faceDescription || '',
        hair: result.hair || '',
        expression: result.expression || '',
        skinTone: result.skinTone || '',
        ageRange: result.ageRange || '',
        ethnicityHint: result.ethnicityHint || '',
        clothing: result.clothing || '',
        pose: result.pose || '',
      };
      patch.character = character;
    }

    // Image references
    const refs: ImageReferences = {
      styleReference: result.intent === 'style_reference' ? (result.style || '') : '',
      characterReference: (result.intent === 'character_reference' || result.intent === 'face_transform' || result.intent === 'face_swap')
        ? (result.faceDescription || result.subject || '') : '',
      environmentReference: result.environment || '',
      compositionReference: result.camera || '',
    };
    if (Object.values(refs).some(Boolean)) {
      patch.references = refs;
    }

    updateSceneState(patch);

    // Also update canvas elements from analysis
    setElements(prev => {
      let next = [...prev];
      if (result.subject && result.intent !== 'style_reference') {
        const existing = next.find(el => el.type === 'character');
        if (existing) {
          next = next.map(el => el.id === existing.id
            ? { ...el, label: result.subject, properties: { ...el.properties, subject: result.subject, pose: result.pose || el.properties.pose, expression: result.expression || el.properties.expression } }
            : el);
        } else {
          next.push({
            id: genId(),
            type: 'character',
            x: 46, y: 52, scale: 1,
            label: result.subject,
            properties: { ...defaultProps.character, subject: result.subject, pose: result.pose || 'standing', expression: result.expression || 'neutral' },
          });
        }
      }
      return next;
    });
  }, [updateSceneState]);

  // ─── State-based Fix API ───

  const buildFixContext = useCallback((): SceneFixContext => ({
    elements: JSON.parse(JSON.stringify(elements)),
    globalSettings: { ...globalSettings },
    aspectRatio,
    stylePreset,
    sceneDescription,
  }), [elements, globalSettings, aspectRatio, stylePreset, sceneDescription]);

  const dedupeDescription = (text: string): string => {
    const parts = text.split(',').map(p => p.trim()).filter(Boolean);
    return Array.from(new Set(parts)).join(', ');
  };

  const applyFixResult = useCallback((result: SceneFixResult, overrideDescription?: string) => {
    setElements(result.elements);
    setGlobalSettings(result.globalSettings);
    if (result.aspectRatio !== aspectRatio) setAspectRatioState(result.aspectRatio);
    if (result.stylePreset !== stylePreset) setStylePresetState(result.stylePreset);
    const desc = overrideDescription !== undefined ? overrideDescription : result.sceneDescription;
    if (desc !== sceneDescription) {
      setSceneDescriptionState(dedupeDescription(desc));
    }
    syncSceneStateFromUI({
      nextElements: result.elements,
      nextGlobalSettings: result.globalSettings,
      nextAspectRatio: result.aspectRatio,
      nextStylePreset: result.stylePreset,
      nextSceneDescription: dedupeDescription(desc),
    });
  }, [aspectRatio, stylePreset, sceneDescription, syncSceneStateFromUI]);

  const getSceneHealth = useCallback((): PromptHealthReport => {
    return analyzeSceneHealth(elements, globalSettings, aspectRatio, stylePreset, prompt);
  }, [elements, globalSettings, aspectRatio, stylePreset, prompt]);

  const fixSingleSceneIssue = useCallback((request: SingleFixRequest) => {
    const ctx = buildFixContext();
    const health = getSceneHealth();
    const result = fixSingleIssue(ctx, request, health);
    applyFixResult(result);
    return result;
  }, [buildFixContext, getSceneHealth, applyFixResult]);

  const fixPromptIssueFromText = useCallback((issue: PromptIssue) => {
    const tokenGroup = mapIssueToTokenGroup(issue);
    if (tokenGroup) {
      return fixSingleSceneIssue({ tokenGroup });
    }
    return fixSingleSceneIssue({ textIssue: issue });
  }, [fixSingleSceneIssue]);

  const fixAllSceneIssues = useCallback(() => {
    // Pre-sanitize sceneDescription to strip technical tokens already covered by elements
    const coveredGroups: Array<import('@/engine/tokenRegistry').TokenGroup> = [];
    if (elements.some(el => el.type === 'camera')) {
      coveredGroups.push('camera_lens', 'camera_perspective', 'camera_shot_type', 'camera_body', 'camera_sensor', 'dof_mode');
    }
    if (elements.some(el => el.type === 'light')) {
      coveredGroups.push('color_temperature', 'light_style', 'light_preset');
    }
    coveredGroups.push('aspect_ratio', 'render_style');
    
    const cleanedDescription = coveredGroups.length > 0
      ? sanitizeSceneDescription(sceneDescription, coveredGroups)
      : sceneDescription;
    
    const result = fixAllIssues(elements, globalSettings, aspectRatio, stylePreset, cleanedDescription, prompt);

    // after the structural fix, run the generator to produce an optimized prompt
    const optimized = generatePrompt(
      result.elements,
      result.aspectRatio,
      result.globalSettings,
      result.sceneDescription,
      result.stylePreset,
      promptFormat,
      palettePreset,
      customPalette,
    ).text;

    applyFixResult(result, optimized);
    return result;
  }, [elements, globalSettings, aspectRatio, stylePreset, sceneDescription, prompt, applyFixResult, promptFormat, palettePreset, customPalette]);

  return {
    elements, aspectRatio, setAspectRatio,
    selectedId, setSelectedId, selected,
    addElement, ensureElement, moveElement, scaleElement,
    updateProperty, removeElement,
    prompt, conflicts, inferred, locked, vfxLayers, vfxIntensity, styleTendencies,
    history, saveToHistory, deleteHistoryEntry, loadHistoryEntry,
    sceneDescription, setSceneDescription,
    stylePreset, setStylePreset,
    palettePreset, setPalettePreset,
    customPalette, setCustomPalette,
    promptFormat, setPromptFormat,
    mode, setMode,
    globalSettings, updateGlobal,
    magneticGuides,
    applyPromptDraft,
    sceneState,
    resetAll,
    // Cinematic Pipeline
    currentSceneGraph,
    pipelineResult,
    // State-based fix API
    getSceneHealth,
    fixSingleSceneIssue,
    fixPromptIssueFromText,
    fixAllSceneIssues,
    handleImageAnalysis,
  };
}
